import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import {
  Person,
  CalendarMonth,
  MedicalServices,
  DateRange,
  Warning,
  Check,
} from '@mui/icons-material';
import { Attestation, AttestationStatus } from '../../types/attestation';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';

interface AttestationListProps {
  attestations: Attestation[];
}

type AttestationStatusLabel =
  | 'Novo'
  | 'Prescrito'
  | 'Análise'
  | 'Revisão'
  | 'Aprovado'
  | 'Reprovado'
  | 'Descartado'
  | 'Desconhecido';

function parseStatus(status: AttestationStatus | number): AttestationStatusLabel {
  const code = Number(status);
  switch (code) {
    case 1:
      return 'Novo';
    case 2:
      return 'Prescrito';
    case 3:
      return 'Análise';
    case 4:
      return 'Revisão';
    case 5:
      return 'Aprovado';
    case 6:
      return 'Reprovado';
    case 8:
      return 'Descartado';
    case 7:
      return 'Aprovado';
    default:
      return 'Desconhecido';
  }
}

const getStatusColor = (status: AttestationStatusLabel) => {
  switch (status) {
    case 'Análise':
      return { bg: '#f0da18', color: '#333' };
    case 'Aprovado':
      return { bg: '#2eab60', color: '#fff' };
    case 'Reprovado':
      return { bg: '#f26849', color: '#fff' };
    case 'Descartado':
      return { bg: '#ffc2a1', color: '#333' };
    default:
      return { bg: '#f1f5f9', color: '#475569' };
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatDateOnly = (dateString: string) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const calculateDays = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays === 0 ? 1 : diffDays + 1;
};

const isWithinPeriod = (startDate: string, endDate: string): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  return today >= start && today <= end;
};

export const AttestationList = ({ attestations }: AttestationListProps) => {
  if (attestations.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          px: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#64748b',
            fontWeight: 500,
          }}
        >
          Nenhum atestado encontrado com os filtros aplicados.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {attestations.map((attestation, index) => {
        const parsedStatus = parseStatus(attestation.Status);
        const statusColors = getStatusColor(parsedStatus);
        const cardKey = `${attestation.id}-${attestation.startDate}-${attestation.endDate}-${attestation.submissionDate}-${index}`;


        return (
          <Card
            key={cardKey}
            sx={{
              position: 'relative',
              overflow: 'visible',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
              borderRadius: 4,
              border: '1px solid rgba(255, 255, 255, 0.6)',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
              },
            }}
          >
            <CardContent sx={{ position: 'relative', p: 3 }}>
              {/* Status Chip - Top Right */}
              <Chip
                label={parsedStatus}
                data-status={parsedStatus}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  backgroundColor: statusColors.bg,
                  color: statusColors.color,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  borderRadius: 2,
                  px: 0.5,
                  height: 28,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              />

              <Box sx={{ pr: { xs: 0, sm: 12 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: '#0f172a',
                    mb: 2.5,
                    fontSize: '1.15rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    pr: { xs: 10, sm: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
                    }}
                  >
                    <Person sx={{ fontSize: '1.2rem', color: '#fff' }} />
                  </Box>
                  {attestation.employeeName}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CalendarMonth sx={{ fontSize: '1.1rem', color: '#94a3b8' }} />
                    <Typography component="span" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                      Enviado:
                    </Typography>
                    <Typography component="span" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                      {formatDate(attestation.submissionDate)}
                    </Typography>
                  </Box>

                  {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <MedicalServices sx={{ fontSize: '1.1rem', color: '#94a3b8' }} />
                    <Typography component="span" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                      Tipo:
                    </Typography>
                    <Typography component="span" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                      {attestation.type}
                    </Typography>
                  </Box> */}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DateRange sx={{ fontSize: '1.1rem', color: '#94a3b8' }} />
                    <Typography component="span" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                      Período:
                    </Typography>
                    <Typography component="span" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                      {attestation.tipo === 'H'
                        ? `${formatDate(attestation.startDate)} - ${formatDate(attestation.endDate)}`
                        : `${formatDateOnly(attestation.startDate)} - ${formatDateOnly(attestation.endDate)}`}
                    </Typography>
                  </Box>

                  {(attestation.tipo === 'D' || !attestation.tipo) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <EventRepeatIcon sx={{ fontSize: '1.1rem', color: '#94a3b8' }} />
                      <Typography component="span" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem' }}>
                        Quantidade de dias:
                      </Typography>
                      <Typography component="span" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                        {calculateDays(attestation.startDate, attestation.endDate)} {calculateDays(attestation.startDate, attestation.endDate) === 1 ? 'dia' : 'dias'}
                      </Typography>
                    </Box>
                  )}

                  {isWithinPeriod(attestation.startDate, attestation.endDate) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, whiteSpace: 'nowrap' }}>
                      <Check sx={{ fontSize: '1.1rem', color: '#94a3b8' }} />
                      <Typography component="span" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        Atestado com data de hoje:
                      </Typography>
                      <Box
                        sx={{
                          backgroundColor: '#dc2626',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 1.5,
                          boxShadow: '0 2px 8px rgba(220, 38, 38, 0.3)',
                          letterSpacing: '0.5px',
                        }}
                      >
                        SIM
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {attestation.Status === 6 && (attestation.Motivo || attestation.MotivoDescricao || attestation.SubMotivoDescricao) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
                  {attestation.MotivoDescricao && (
                    <Box sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1.5,
                      background: 'linear-gradient(135deg, rgba(254, 243, 199, 0.5) 0%, rgba(254, 249, 195, 0.3) 100%)',
                      border: '1px solid rgba(234, 179, 8, 0.2)',
                      borderRadius: 3, px: 2.5, py: 1.5,
                    }}>
                      <Warning sx={{ fontSize: '1.1rem', color: '#d97706', mt: 0.25 }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.8rem', mb: 0.25 }}>Motivo:</Typography>
                        <Typography sx={{ color: '#78350f', fontSize: '0.85rem', lineHeight: 1.4 }}>{attestation.MotivoDescricao}</Typography>
                      </Box>
                    </Box>
                  )}
                  {attestation.SubMotivoDescricao && (
                    <Box sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1.5,
                      background: 'linear-gradient(135deg, rgba(219, 234, 254, 0.5) 0%, rgba(239, 246, 255, 0.3) 100%)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      borderRadius: 3, px: 2.5, py: 1.5,
                    }}>
                      <Warning sx={{ fontSize: '1.1rem', color: '#2563eb', mt: 0.25 }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.8rem', mb: 0.25 }}>Submotivo:</Typography>
                        <Typography sx={{ color: '#1e3a8a', fontSize: '0.85rem', lineHeight: 1.4 }}>{attestation.SubMotivoDescricao}</Typography>
                      </Box>
                    </Box>
                  )}
                  {attestation.Motivo && !attestation.MotivoDescricao && (
                    <Box sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1.5,
                      background: 'linear-gradient(135deg, rgba(254, 243, 199, 0.5) 0%, rgba(254, 249, 195, 0.3) 100%)',
                      border: '1px solid rgba(234, 179, 8, 0.2)',
                      borderRadius: 3, px: 2.5, py: 1.5,
                    }}>
                      <Warning sx={{ fontSize: '1.1rem', color: '#d97706', mt: 0.25 }} />
                      <Box>
                        <Typography sx={{ fontWeight: 600, color: '#92400e', fontSize: '0.8rem', mb: 0.25 }}>Motivo:</Typography>
                        <Typography sx={{ color: '#78350f', fontSize: '0.85rem', lineHeight: 1.4 }}>{attestation.Motivo}</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box >
  );
};
